import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MapPin, Search, Navigation, Filter, Award } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { sportBranches, cities, getDistricts } from '@/data/turkeyData';

// Fix Leaflet icon issue
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
});

L.Marker.prototype.options.icon = DefaultIcon;

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/(api)?\/?$/, "")}/api`;

const MapSearchPage = () => {
  const navigate = useNavigate();
  const [facilities, setFacilities] = useState([]);
  const [userLocation, setUserLocation] = useState(null); // { lat, lng }
  const [loading, setLoading] = useState(false);
  const [mapCenter, setMapCenter] = useState([39.9334, 32.8597]); // Ankara default
  const [mapZoom, setMapZoom] = useState(6);
  
  // Filters
  const [city, setCity] = useState('');
  const [district, setDistrict] = useState('');
  const [branch, setBranch] = useState('');
  const [availableDistricts, setAvailableDistricts] = useState([]);

  useEffect(() => {
    fetchFacilities();
  }, [city, district, branch]); // Refetch when filters change

  useEffect(() => {
    if (city) {
      setAvailableDistricts(getDistricts(city));
      // Focus map on city (approximate center for major cities or keep current)
      // Ideally we would geocode the city name to center map, but for now we rely on markers
    } else {
      setAvailableDistricts([]);
    }
  }, [city]);

  const fetchFacilities = async () => {
    try {
        const params = new URLSearchParams();
        params.append('status', 'approved');
        if (city) params.append('city', city);
        if (district) params.append('district', district);
        if (branch) params.append('branch', branch);

      const response = await axios.get(`${API}/facilities?${params.toString()}`);
      setFacilities(response.data);
    } catch (error) {
      console.error('Error fetching facilities:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGetUserLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Tarayıcınız konum özelliğini desteklemiyor');
      return;
    }

    toast.info('Konum alınıyor...');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ lat: latitude, lng: longitude });
        setMapCenter([latitude, longitude]);
        setMapZoom(13);
        toast.success('Konum bulundu!');
      },
      (error) => {
        console.error('Geolocation error:', error);
        toast.error('Konum alınamadı. Lütfen izinleri kontrol edin.');
      }
    );
  };

  // Helper to create custom ball icon based on branch
  const createCustomIcon = (facility) => {
    // Default ball (football)
    let ballEmoji = '⚽';
    let color = 'text-emerald-600';
    
    // Check facility branches
    if (facility.branches && facility.branches.length > 0) {
        if (facility.branches.includes('basketbol')) {
            ballEmoji = '🏀';
            color = 'text-orange-600';
        } else if (facility.branches.includes('voleybol')) {
            ballEmoji = '🏐';
            color = 'text-blue-600';
        } else if (facility.branches.includes('tenis')) {
            ballEmoji = '🎾';
            color = 'text-yellow-600';
        }
    } else if (facility.facility_type === 'kapali_spor_salonu') {
        ballEmoji = '🏟️';
        color = 'text-purple-600';
    } else if (facility.facility_type === 'plaj_voleybolu') {
        ballEmoji = '🏖️';
        color = 'text-cyan-600';
    } else if (facility.facility_type === 'masa_tenisi') {
        ballEmoji = '🏓';
        color = 'text-rose-600';
    }

    // Create a custom div icon
    return L.divIcon({
        className: 'custom-map-marker',
        html: `<div class="relative">
                 <div class="w-10 h-10 bg-white rounded-full border-2 border-white shadow-lg flex items-center justify-center text-xl transform -translate-x-1/2 -translate-y-full">
                    ${ballEmoji}
                 </div>
                 <div class="w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-t-[12px] border-t-white absolute left-[-8px] bottom-[-10px]"></div>
               </div>`,
        iconSize: [40, 50],
        iconAnchor: [20, 50],
        popupAnchor: [0, -50]
    });
  };

  // Component to update map view when center changes
  const MapUpdater = () => {
    const map = useMap();
    useEffect(() => {
      map.setView(mapCenter, mapZoom);
    }, [mapCenter, mapZoom, map]);
    return null;
  };

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col md:flex-row">
      {/* Sidebar List & Filters */}
      <div className="w-full md:w-1/3 lg:w-1/4 bg-white border-r border-slate-200 flex flex-col h-full">
        <div className="p-4 border-b border-slate-200 shrink-0">
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2 mb-4">
            <MapPin className="w-6 h-6 text-emerald-600" />
            Harita ile Ara
          </h1>
          
          <div className="space-y-3">
            {/* City Filter */}
            <Select value={city} onValueChange={(v) => { setCity(v); setDistrict(''); }}>
                <SelectTrigger>
                    <SelectValue placeholder="Şehir Seçiniz" />
                </SelectTrigger>
                <SelectContent>
                    {cities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
            </Select>

            {/* District Filter */}
            <Select value={district} onValueChange={setDistrict} disabled={!city}>
                <SelectTrigger>
                    <SelectValue placeholder="İlçe Seçiniz" />
                </SelectTrigger>
                <SelectContent>
                    {availableDistricts.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
            </Select>

            {/* Branch Filter */}
            <Select value={branch} onValueChange={setBranch}>
                <SelectTrigger>
                    <SelectValue placeholder="Spor Dalı" />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="all">Tümü</SelectItem>
                    {sportBranches.map(b => (
                        <SelectItem key={b.id} value={b.id}>{b.icon} {b.label}</SelectItem>
                    ))}
                </SelectContent>
            </Select>

            <Button 
                onClick={handleGetUserLocation}
                className="w-full bg-emerald-700 hover:bg-emerald-800"
            >
                <Navigation className="w-4 h-4 mr-2" />
                Yakınımdaki Tesisleri Bul
            </Button>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <p className="text-sm text-slate-500 font-medium">{facilities.length} tesis bulundu</p>
          {facilities.map(facility => (
            <Card 
              key={facility.facility_id} 
              className="cursor-pointer hover:border-emerald-500 transition-all"
              onClick={() => {
                if (facility.latitude && facility.longitude) {
                  setMapCenter([facility.latitude, facility.longitude]);
                  setMapZoom(15);
                } else {
                  navigate(`/tesis/${facility.facility_id}`);
                }
              }}
            >
              <CardContent className="p-3 flex gap-3">
                {facility.images?.[0] ? (
                  <img src={facility.images[0]} alt="" className="w-20 h-20 object-cover rounded-lg bg-slate-200" />
                ) : (
                  <div className="w-20 h-20 bg-slate-200 rounded-lg flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-slate-400" />
                  </div>
                )}
                <div>
                  <h3 className="font-bold text-slate-900 line-clamp-1">{facility.name}</h3>
                  {facility.is_partner && (
                    <Badge className="bg-amber-500 text-white shadow-sm flex items-center gap-1 w-fit mt-1 mb-1 text-[10px] h-5 px-1.5">
                      <Award className="w-3 h-3" />
                      Partner
                    </Badge>
                  )}
                  {!facility.is_partner && (
                    <Badge variant="secondary" className="bg-slate-100 text-slate-500 shadow-sm w-fit mt-1 mb-1 text-[10px] h-5 px-1.5 opacity-80">
                      Partner Değil
                    </Badge>
                  )}
                  <p className="text-xs text-slate-500">{facility.district}, {facility.city}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-sm font-semibold text-emerald-600">{facility.price_per_hour} ₺/saat</p>
                    {facility.branches && facility.branches[0] && (
                        <span className="text-xs bg-slate-100 px-1.5 py-0.5 rounded text-slate-600">
                            {sportBranches.find(b => b.id === facility.branches[0])?.icon || '⚽'}
                        </span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Map Area */}
      <div className="flex-1 relative bg-slate-100">
        <MapContainer 
          center={mapCenter} 
          zoom={mapZoom} 
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapUpdater />
          
          {userLocation && (
            <Marker position={[userLocation.lat, userLocation.lng]}>
              <Popup>Konumunuz</Popup>
            </Marker>
          )}

          {facilities.map(facility => {
            // Use real coordinates if available
            if (facility.latitude && facility.longitude) {
              return (
                <Marker 
                  key={facility.facility_id} 
                  position={[facility.latitude, facility.longitude]}
                  icon={createCustomIcon(facility)}
                >
                  <Popup>
                    <div className="p-1 min-w-[200px]">
                      <div className="flex justify-between items-start mb-1">
                        <h3 className="font-bold text-base">{facility.name}</h3>
                        {facility.is_partner ? (
                          <div className="bg-amber-500 text-white text-[10px] px-1.5 py-0.5 rounded flex items-center gap-0.5 shadow-sm">
                            <span className="text-xs">🏆</span> Partner
                          </div>
                        ) : (
                          <div className="bg-slate-100 text-slate-500 text-[10px] px-1.5 py-0.5 rounded border border-slate-200">
                            Partner Değil
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 mb-2">{facility.district}, {facility.city}</p>
                      <div className="flex justify-between items-center mb-3">
                        <span className="font-semibold text-emerald-600">{facility.price_per_hour} ₺</span>
                        <div className="flex gap-1">
                            {facility.branches?.slice(0, 3).map(b => (
                                <span key={b} className="text-lg" title={b}>
                                    {sportBranches.find(sb => sb.id === b)?.icon || '⚽'}
                                </span>
                            ))}
                        </div>
                      </div>
                      <Button 
                        size="sm" 
                        className="w-full bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => navigate(`/tesis/${facility.facility_id}`)}
                      >
                        Tesisi İncele
                      </Button>
                    </div>
                  </Popup>
                </Marker>
              );
            }
            return null;
          })}
        </MapContainer>
        
        {/* Helper Badge */}
        <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur px-4 py-2 rounded-lg shadow-lg z-[1000] text-xs text-slate-600">
          Harita üzerindeki toplara tıklayarak detayları görebilirsiniz.
        </div>
      </div>
    </div>
  );
};

export default MapSearchPage;
